import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowUp,
  Bell,
  BellOff,
  Flag,
  Flame,
  Medal,
  Shield,
  TriangleAlert,
  Trophy,
  UserPlus,
} from 'lucide-react';
import { useNotifications } from '../../contexts/NotificationContext';
import './NotificationPanel.css';

const NOTIFICATION_URL_MAP = {
  achievement_unlocked: '/achievements',
  level_up: '/dashboard',
  streak_milestone: '/dashboard',
  streak_shield: '/dashboard',
  streak_warning: '/dashboard',
  challenge_completed: '/dashboard',
  goal_completed: '/progress',
  new_follower: '/leaderboard',
};

const NOTIFICATION_ICON_MAP = {
  achievement_unlocked: Trophy,
  level_up: ArrowUp,
  streak_milestone: Flame,
  streak_shield: Shield,
  streak_warning: TriangleAlert,
  challenge_completed: Medal,
  goal_completed: Flag,
  new_follower: UserPlus,
};

function timeAgo(dateStr) {
  const seconds = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export default function NotificationPanel({ onClose, returnFocusRef }) {
  const navigate = useNavigate();
  const panelRef = useRef(null);
  const { notifications, loading, fetchNotifications, markAsRead, markAllAsRead, unreadCount } = useNotifications();

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose();
      }
    };
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      onClose();
      window.requestAnimationFrame(() => returnFocusRef?.current?.focus());
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [onClose, returnFocusRef]);

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    const url = notification.data?.url || NOTIFICATION_URL_MAP[notification.type] || '/dashboard';
    navigate(url);
    onClose();
  };

  return (
    <div id="notification-panel" className="notification-panel" ref={panelRef} role="region" aria-label="Notifications">
      <div className="notification-panel-header">
        <h3>Notifications</h3>
        {unreadCount > 0 && (
          <button type="button" className="notification-mark-all" onClick={markAllAsRead}>
            Mark all read
          </button>
        )}
      </div>

      <div className="notification-panel-list" aria-live="polite">
        {loading && notifications.length === 0 && (
          <div className="notification-empty" role="status">Loading...</div>
        )}

        {!loading && notifications.length === 0 && (
          <div className="notification-empty">
            <BellOff size={32} aria-hidden="true" />
            <p>No notifications yet</p>
          </div>
        )}

        {notifications.map((n) => {
          const NotificationIcon = NOTIFICATION_ICON_MAP[n.type] || Bell;
          return (
            <button
              key={n.id}
              type="button"
              className={`notification-item ${n.read ? '' : 'notification-unread'}`}
              onClick={() => handleNotificationClick(n)}
            >
              <NotificationIcon className="notification-icon" size={20} aria-hidden="true" />
              <div className="notification-content">
                <span className="notification-title">{n.title}</span>
                <span className="notification-body">{n.body}</span>
                <span className="notification-time">{timeAgo(n.created_at)}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
